import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { SyncPhase } from '../../types/sync/enums';
import { formatElapsedDuration } from '../../utils/formatters';
import { IconCheckCircle } from '../../assets/icons/IconCheckCircle';
import { Spinner } from '../../ui/Spinner/Spinner';
import { PageTitle } from '../../ui/PageTitle/PageTitle';
import { useAuthStore } from '../../stores/authStore';
import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { useIsAcomba } from '../../hooks/useIsAcomba';
import { getTimeOfDay, getFirstName, TimeOfDay } from '../../utils/greeting';
import { useElapsedSeconds } from './useElapsedSeconds';
import { isPhaseAtOrAfter } from '../../utils/sync/syncPhase';
import type { SyncChecklistProps } from './types';

interface ChecklistItem {
    key: string;
    done: boolean;
    labelKey: string;
}

/**
 * One checklist row: a filled checkmark once done, a spinner while not, and the running elapsed
 * time on it only while it is the current (first not-done) item — never on one already completed.
 */
function ChecklistItemRow({ item, elapsedLabel }: { item: ChecklistItem; elapsedLabel: { key: string; options?: Record<string, unknown> } | null }) {
    const { t } = useTranslation();
    return (
        <div data-testid={`home-sync-checklist-item-${item.key}`} className="flex items-center gap-s text-m font-normal">
            {item.done ? (
                <IconCheckCircle className="size-4 shrink-0 text-icon-muted" data-testid={`home-sync-checklist-check-${item.key}`} />
            ) : (
                <span className="flex size-4 shrink-0 items-center justify-center">
                    <Spinner className="size-text" testId={`home-sync-checklist-spinner-${item.key}`} />
                </span>
            )}
            <span data-testid={`home-sync-checklist-label-${item.key}`} className={item.done ? 'font-medium text-content-subtle' : 'text-content-strong'}>{t(item.labelKey)}</span>
            {elapsedLabel && (
                <span data-testid={`home-sync-checklist-elapsed-${item.key}`} className="text-m font-semibold text-content-secondary">
                    {t(elapsedLabel.key, elapsedLabel.options)}
                </span>
            )}
        </div>
    );
}

/**
 * The progress display for every sync: a salutation (mirroring HomeGreeting's part 1) followed by
 * items that appear one at a time, in strict order. Leaving `Preprocessing` checks "dossiers analysés";
 * "préparation des relances" checks once every planned relance has completed, with a final
 * never-checked "finalizing" row for the (usually brief) gap before the caller unmounts this
 * component at RelanceFill.
 */
export const SyncChecklist: React.FC<SyncChecklistProps> = ({ phase, completed, expected }) => {
    const { t } = useTranslation();
    const displayName = useAuthStore(s => s.displayName);
    const user = useAuthStore(s => s.user);
    const isAcomba = useIsAcomba();
    const bridgeUserName = useAcombaBridgeStore(s => s.sessionInfo?.userName ?? null);

    const nameSource = displayName ?? (isAcomba ? bridgeUserName : null) ?? user?.name;
    const firstName = getFirstName(nameSource);
    const timeOfDay = getTimeOfDay();
    const salutationKey =
        timeOfDay === TimeOfDay.Morning   ? TranslationKey.Home.Greeting.Morning :
        timeOfDay === TimeOfDay.Afternoon ? TranslationKey.Home.Greeting.Afternoon :
                                            TranslationKey.Home.Greeting.Evening;
    const salutation = t(salutationKey, { name: firstName ? ` ${firstName}` : '' });

    const foldersAnalyzedNow = isPhaseAtOrAfter(phase, SyncPhase.PriorityRanking);
    // Latched: this component stays mounted for as long as the checklist is open, well past the
    // point where `phase` itself may go stale or reset — reading it live here would flip "Dossiers
    // analysés" back to a spinner even though it genuinely already completed.
    const [foldersAnalyzed, setFoldersAnalyzed] = useState(foldersAnalyzedNow);
    useEffect(() => {
        if (foldersAnalyzedNow) setFoldersAnalyzed(true);
    }, [foldersAnalyzedNow]);
    // PriorityRanking (and RelanceFill) arrive independently of the relance fan-out finishing, so
    // phase alone says nothing about relance progress; the counter does.
    const relancesPrepared = completed >= expected;

    const header = (
        <div className="flex flex-col gap-l">
            <PageTitle data-testid="home-sync-checklist-greeting">{salutation}</PageTitle>
            <p className="text-m font-normal text-content">{t(TranslationKey.Home.SyncChecklist.GreetingSubtitle)}</p>
        </div>
    );

    const foldersItem: ChecklistItem = { key: 'folders', done: foldersAnalyzed, labelKey: TranslationKey.Home.SyncChecklist.Items.FoldersAnalyzed };

    // Revealed one at a time, in strict order — never both at once, and never out of sequence.
    const relancesItem: ChecklistItem = { key: 'relances', done: relancesPrepared, labelKey: TranslationKey.Home.SyncChecklist.Items.RelancePreparation };
    // Never done: keeps something animating for the (usually brief) gap between the counter reaching
    // relancesPrepared and the caller unmounting this component at RelanceFill — see the class doc above.
    const finalizingItem: ChecklistItem = { key: 'finalizing', done: false, labelKey: TranslationKey.Home.SyncChecklist.Items.Finalizing };

    const visible: ChecklistItem[] = [foldersItem];
    if (foldersAnalyzed) {
        visible.push(relancesItem);
        if (relancesPrepared) visible.push(finalizingItem);
    }
    // The elapsed clock belongs to whichever item is currently running — the first one still not done.
    const currentKey = visible.find(i => !i.done)?.key ?? null;
    const elapsedSeconds = useElapsedSeconds(currentKey);
    const elapsed = formatElapsedDuration(elapsedSeconds);

    return (
        <div data-testid="home-sync-checklist" className="flex flex-col gap-l">
            {header}
            <div className="flex flex-col gap-s">
                {visible.map(item => (
                    // Seconds are suppressed at 0 rather than flashing "0 sec".
                    <ChecklistItemRow
                        key={item.key}
                        item={item}
                        elapsedLabel={item.key === currentKey && elapsedSeconds > 0 ? elapsed : null}
                    />
                ))}
            </div>
        </div>
    );
};
